/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { Buffer } from 'buffer';
import { getDefaultStore } from 'jotai';
import {
  adminSettingsIndyRaceProgressionOriginAtom,
  adminSettingsNascarRaceProgressionOriginAtom,
} from 'jotai/adminPage/adminSettings';
import { activeSanctioningBodyFeedAtom, isIndyCarSanctioningBodySelector } from 'jotai/sanctioningBody';
import { activeEventSelector, activeSessionSelector } from 'jotai/session';
import { isMobileWorkspaceAtom } from 'jotai/workspace';
import { OriginType, RunType, SanctioningBodyEnum, StreamInfo } from 'models/viewModels';
import {
  WellKnownMimeType,
  encodeBearerAuthMetadata,
  encodeCompositeMetadata,
  encodeRoute,
} from 'rsocket-composite-metadata';
import { RSocket, RSocketConnector } from 'rsocket-core';
import { WebsocketClientTransport } from 'rsocket-websocket-client';
import { authService } from 'services/auth/AuthService';
import { v4 as uuidv4 } from 'uuid';
import {
  APPLICATION_CBOR,
  MESSAGE_RSOCKET_AUTHENTICATION,
  MESSAGE_RSOCKET_COMPOSITE_METADATA,
  MESSAGE_RSOCKET_ROUTING,
  RSocketBaseServiceInterface,
  RSocketChannelClientInterface,
  RSocketStreamClientInterface,
  StreamRequestType,
} from './RSocketObject';

// This map is used to determine the base websocket URL for the RSocket connection based on the sanctioning body and stream request type
const RSocketConnectionTypeMap: Record<SanctioningBodyEnum, Record<StreamRequestType, string>> = {
  [SanctioningBodyEnum.NASCAR]: {
    [StreamRequestType.MotorsportsStreamApi]: `${process.env.REACT_APP_STREAM_API_WS_URL}/rsocket`,
    [StreamRequestType.DataScienceStreamApi]: `${process.env.REACT_APP_DS_API_WS_URL}/rsocket`,
  },
  [SanctioningBodyEnum.INDYCAR]: {
    [StreamRequestType.MotorsportsStreamApi]: `${process.env.REACT_APP_INDY_STREAM_API_WS_URL}/rsocket`,
    // TODO: Update when we support IndyCar data science stream
    [StreamRequestType.DataScienceStreamApi]: `${process.env.REACT_APP_DS_API_WS_URL}/rsocket`,
  },
  [SanctioningBodyEnum.IMSA]: {
    //TODO: Update when we support IMSA streams
    [StreamRequestType.MotorsportsStreamApi]: `${process.env.REACT_APP_STREAM_API_WS_URL}/rsocket`,
    [StreamRequestType.DataScienceStreamApi]: `${process.env.REACT_APP_DS_API_WS_URL}/rsocket`,
  },
  [SanctioningBodyEnum.F1]: {
    //TODO: Update when we support F1 streams
    [StreamRequestType.MotorsportsStreamApi]: `${process.env.REACT_APP_STREAM_API_WS_URL}/rsocket`,
    [StreamRequestType.DataScienceStreamApi]: `${process.env.REACT_APP_DS_API_WS_URL}/rsocket`,
  },
  [SanctioningBodyEnum.UNKNOWN]: {
    [StreamRequestType.MotorsportsStreamApi]: `${process.env.REACT_APP_STREAM_API_WS_URL}/rsocket`,
    [StreamRequestType.DataScienceStreamApi]: `${process.env.REACT_APP_DS_API_WS_URL}/rsocket`,
  },
};

const defaultStore = getDefaultStore();
interface constructorProps {
  route: string;
  connectionType: StreamRequestType;
  originType?: OriginType;
  sanctioningBody: SanctioningBodyEnum;
}

// T is the local cache type, RESP is the response type
// Local cache is used to store the stream data and gets passed to listeners
// Response type is the type of the data received from the stream
export abstract class RSocketBaseService<T, RESP> implements RSocketBaseServiceInterface {
  /**
   * Stream management
   */
  protected stream: RSocketStreamClientInterface | RSocketChannelClientInterface | undefined;
  private on: boolean = false;
  private intervalId?: NodeJS.Timeout;
  readonly retryDelay = 5 * 1000;
  readonly streamCompleteRetryDelay = 30 * 1000;

  /**
   * Route management
   */
  protected route: string;
  protected originType?: OriginType;
  protected connnectionType: StreamRequestType = StreamRequestType.MotorsportsStreamApi;
  protected sanctioningBody: SanctioningBodyEnum = SanctioningBodyEnum.UNKNOWN;
  protected request: any;

  /**
   * Socket management
   */
  protected static socket: Map<SanctioningBodyEnum, Map<StreamRequestType, RSocket | undefined>> = new Map(
    (Object.values(SanctioningBodyEnum) as SanctioningBodyEnum[])
      .filter((x) => typeof x === 'number')
      .map((sanctioningBody: SanctioningBodyEnum) => [
        sanctioningBody,
        new Map<StreamRequestType, RSocket | undefined>([
          [StreamRequestType.MotorsportsStreamApi, undefined],
          [StreamRequestType.DataScienceStreamApi, undefined],
        ]),
      ])
  );

  // This map is used to determine if a socket connection exists based on the sanctioning body and stream request type so we don't create multiple connections
  private static socketConnectionExists: Map<SanctioningBodyEnum, Map<StreamRequestType, boolean>> = new Map(
    (Object.values(SanctioningBodyEnum) as SanctioningBodyEnum[])
      .filter((x) => typeof x === 'number')
      .map((sanctioningBody: SanctioningBodyEnum) => [
        sanctioningBody,
        new Map<StreamRequestType, boolean>([
          [StreamRequestType.MotorsportsStreamApi, false],
          [StreamRequestType.DataScienceStreamApi, false],
        ]),
      ])
  );

  /**
   * Retry Logic management
   */
  protected numOfRequests = 0;
  // Delay in milliseconds
  protected delay: number | undefined;

  /**
   * Stream data management
   */

  /**
   * Local cache is used to store the stream data.
   * It is commonly set on getStreamHistoryRequest and onDataCallback.
   * This value is commonly passed to listeners (e.g. wherever you see addListener) but not always.
   * If you use a delay on the streamService, you must set the localCache on the onDataCallback method
   * or your listeners as localCache is what is being passed to the listeners (if block on line 183)
   */
  protected localCache: T = [] as unknown as T;

  /**
   * Listeners are ways for atoms to be notified with stream messages.
   * BaseService subclasses are intended to reference
   * and execute the listener callbacks in the onDataCallback method.
   *
   * Adding listeners will attempt to start the stream.
   */

  protected listeners: Map<string, (object: T) => void> = new Map<string, (object: T) => void>();
  protected resetListeners: Map<string, () => void> = new Map<string, () => void>();

  // Defined by the implementing class
  protected abstract onDataCallback: (data: RESP) => void;

  constructor(props: constructorProps) {
    this.route = props.route;
    this.connnectionType = props.connectionType;
    this.originType = props.originType;
    this.sanctioningBody = props.sanctioningBody;
    // skip creating socket if the sanctioningBody is not NASCAR or INDYCAR
    // TODO: Remove this when we support other sanctioning bodies
    if (props.sanctioningBody === SanctioningBodyEnum.NASCAR || props.sanctioningBody === SanctioningBodyEnum.INDYCAR) {
      RSocketBaseService.createSocket(props.connectionType, props.sanctioningBody);
    }
  }

  // This method is used to get the backfill data for the stream
  protected abstract getStreamHistoryRequest(input?: any): Promise<T | undefined>;

  // This method is called whenever a listener is added or removed
  // isValid is used to determine if the backfill data is valid (AKA not an array)
  protected async start(isValid?: boolean) {
    this.request = await this.getStreamRequest();
    const backfill = await this.getStreamHistoryRequest();

    // If the backfill is valid, set the local cache and notify listeners (e.g. addListener callbacks in Jotai atoms or streamManagements)
    if (backfill && (isValid || (Array.isArray(backfill) && backfill.length > 0))) {
      this.localCache = backfill;
      this.listeners.forEach((listener) => listener(this.localCache));
    }

    if (this.delay) {
      this.intervalId = setInterval(() => {
        if (this.listeners.size > 0 && (isValid || (Array.isArray(this.localCache) && this.localCache.length > 0))) {
          // Notify listeners with local cache (e.g. addListener callbacks in Jotai atoms or streamManagements)
          this.listeners.forEach((listener) => listener(this.localCache));
        }
      }, this.delay);
    }

    // If the stream is already running, stop it
    if (this.stream) {
      this.cancel();
    }

    this.on = true;

    this.stream = await this.createStream();
  }

  private static createSocket(connectionType: StreamRequestType, sanctioningBody: SanctioningBodyEnum) {
    // Create socket connection if it doesn't exist
    if (
      !RSocketBaseService.socketConnectionExists.get(sanctioningBody)?.get(connectionType) &&
      RSocketBaseService.socket.get(sanctioningBody)?.get(connectionType) === undefined
    ) {
      RSocketBaseService.socketConnectionExists.get(sanctioningBody)?.set(connectionType, true);
      console.log(`Creating RSocket connection to ${RSocketConnectionTypeMap[sanctioningBody][connectionType]}`);
      RSocketBaseService.connect(connectionType, sanctioningBody)
        .then((socket) => {
          console.log(`RSocket connection to ${RSocketConnectionTypeMap[sanctioningBody][connectionType]} established`);
          RSocketBaseService.socket.get(sanctioningBody)?.set(connectionType, socket);
          RSocketBaseService.socket
            .get(sanctioningBody)
            ?.get(connectionType)
            ?.onClose(() => {
              RSocketBaseService.reconnectToRSocket(connectionType, sanctioningBody);
            });
        })
        // If the connection fails, attempt to reconnect
        .catch((e) => {
          console.error(`Failed to connect to RSocket: ${e}`);
          RSocketBaseService.reconnectToRSocket(connectionType, sanctioningBody);
        });
    }
  }

  private static reconnectToRSocket(connectionType: StreamRequestType, sanctioningBody: SanctioningBodyEnum) {
    console.log('Reconnecting to RSocket');
    setTimeout(() => {
      RSocketBaseService.socketConnectionExists.get(sanctioningBody)?.set(connectionType, false);
      RSocketBaseService.socket.get(sanctioningBody)?.set(connectionType, undefined);
      RSocketBaseService.createSocket(connectionType, sanctioningBody);
    }, 1000);
  }

  // Builds the RSocket connection based on the connection type and sanctioning body
  private static connect(connectionType: StreamRequestType, sanctioningBody: SanctioningBodyEnum) {
    return new RSocketConnector({
      transport: new WebsocketClientTransport({
        url: RSocketConnectionTypeMap[sanctioningBody][connectionType],
        wsCreator: (url) => new WebSocket(url),
      }),
      setup: {
        keepAlive: 10000,
        lifetime: 20000,
        dataMimeType: APPLICATION_CBOR.string,
        metadataMimeType: MESSAGE_RSOCKET_COMPOSITE_METADATA.string,
      },
    }).connect();
  }

  // Defined in RSocketsStreamBaseService and RSocketChannelBaseService
  protected abstract createStream(): Promise<RSocketStreamClientInterface | RSocketChannelClientInterface | undefined>;

  protected reconnectToStream(isComplete?: boolean) {
    console.log(`Reconnecting to ${this.route}`);
    this.cancel();
    setTimeout(
      () => {
        this.start();
      },
      isComplete ? this.streamCompleteRetryDelay : this.retryDelay
    );
  }

  protected buildMetadata() {
    const map = new Map<WellKnownMimeType, Buffer>();
    map.set(MESSAGE_RSOCKET_ROUTING, encodeRoute(this.route));
    map.set(MESSAGE_RSOCKET_AUTHENTICATION, encodeBearerAuthMetadata(authService.getAccessToken()));
    return encodeCompositeMetadata(map);
  }

  // This method build the request object that will be sent to the stream
  // This method can be overridden by the implmenting class (see AudioDataServiceRSocket.ts for an example)
  protected async getStreamRequest(): Promise<any> {
    const isIndy = defaultStore.get(isIndyCarSanctioningBodySelector);
    // use admin settings to determine origin type
    const adminOriginForRaceProgression = isIndy
      ? defaultStore.get(adminSettingsIndyRaceProgressionOriginAtom)
      : defaultStore.get(adminSettingsNascarRaceProgressionOriginAtom);
    const event = defaultStore.get(activeEventSelector);
    const session = defaultStore.get(activeSessionSelector);
    // originType FEED_STATUS (AKA stream.feedStatus) is a special case where we don't care about runType
    const runType =
      this.originType === OriginType.FEED_STATUS || this.originType === OriginType.WEATHER_STATION
        ? RunType.UNKNOWNRUNTYPE
        : session?.sessionType;
    const activeSanctioningBodyFeed = defaultStore.get(activeSanctioningBodyFeedAtom);
    const isMobile = defaultStore.get(isMobileWorkspaceAtom);
    if (event && session) {
      return {
        raceId: event.eventId,
        runType: runType,
        seriesType: parseInt(session.seriesId),
        originType:
          /**
           * use the origin type if not undefined
           * Race Progression, Live Session are RSocket Services where originType is undefined
           * The chain after that really only affects Race Progression since Live Session doesn't care about the originType
           */
          this.originType !== undefined
            ? this.originType
            : // continuous session or qualifying or Indy AND admin hasn't set an origin, use ML as the origin type
            session.sessionType === RunType.QUALIFYING || (isIndy && !adminOriginForRaceProgression)
            ? OriginType.MULTILOOP
            : // if mobile AND admin hasn't set an origin, use ERDP as origin type
            isMobile && !adminOriginForRaceProgression
            ? OriginType.ERDP
            : adminOriginForRaceProgression
            ? adminOriginForRaceProgression
            : // continuous session, use the active sanctioning body feed
            activeSanctioningBodyFeed
            ? activeSanctioningBodyFeed
            : // not a continuous session, use ERDP as the origin type if admin hasn't set an origin
              OriginType.ERDP,
        sessionId: session.sessionId,
      } as StreamInfo;
    }
  }

  // This method is called when a listener is added or removed
  // If there are no listeners, the stream is stopped
  // If there are listeners, the stream is started
  private checkSwitch() {
    if (this.on && this.listeners.size === 0) {
      console.log('No listeners, stopping stream', { route: this.route, streamRequestData: this.request });
      this.cancel();
    }
    if (!this.on && this.listeners.size > 0) {
      this.start();
    }
  }

  protected cancel() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.stream?.cancel();
    this.stream = undefined;
    this.on = false;
  }

  // This method is called when a listener is added (AKA when the initXXX Jotai atom is called)
  addListener(callback: (object: T) => void, reset: () => void, listenerId?: string): string {
    // If a listenerId is not provided, generate a new one
    const id = listenerId || uuidv4();
    // Add the listenerId to the listeners map and add the callback method (AKA the first method in the addListener method in the initXX Jotai atom)
    this.listeners.set(id, callback);
    // Add the listenerId to the resetListeners map and add the reset method (AKA the second method in the addListener method in the initXX Jotai atom)
    this.resetListeners.set(id, reset);
    // Start the stream if it is not already running
    this.checkSwitch();
    return id;
  }

  removeListener(id: string): boolean {
    const removed = this.listeners.delete(id);
    this.resetListeners.delete(id);
    if (removed) {
      this.checkSwitch();
    }
    return removed;
  }

  removeAllListeners() {
    this.listeners.clear();
    this.resetListeners.clear();
    this.checkSwitch();
  }

  reset(hard: boolean = false) {
    this.localCache = [] as unknown as T;

    if (hard) {
      this.start();
    }

    if (this.resetListeners.size > 0) {
      this.resetListeners.forEach((resetListener) => resetListener());
    }
  }
}
